import type { EvaluationRowSchema } from "@/lib/api/model";
import type { PipelineCandidate } from "@/lib/api/types";

export interface CandidateCellSocialProfile {
  network?: string | null;
  url?: string | null;
}

/** Normalised row the shared table cells render; built by the adapters below. */
export interface CandidateCellRow {
  profileId: string;
  fullName: string | null;
  avatarUrl: string | null;
  origin: string | null;
  jobTitle: string | null;
  companyName: string | null;
  companyDomain: string | null;
  linkedinUrl: string | null;
  githubUrl: string | null;
  twitterUrl: string | null;
  socialProfiles: readonly CandidateCellSocialProfile[];
}

/**
 * Arbitrator `profile_card` payload. The Orval type is `{ [k: string]: unknown }`
 * because the backend keeps the column loose; narrowed here.
 */
export interface ProfileCardLike {
  full_name?: string | null;
  headline?: string | null;
  job_title?: string | null;
  job_company_name?: string | null;
  job_company_domain?: string | null;
  linkedin_url?: string | null;
  twitter_url?: string | null;
  github_url?: string | null;
  avatar_url?: string | null;
  social_profiles?: CandidateCellSocialProfile[] | null;
  origin?: string | null;
}

// Cached per source object: re-rendering with the same source row hands the
// memoised cells the same `CandidateCellRow` reference instead of a fresh one.
const pipelineRows = new WeakMap<PipelineCandidate, CandidateCellRow>();
const arbitratorRows = new WeakMap<EvaluationRowSchema, CandidateCellRow>();

export function fromPipelineCandidate(candidate: PipelineCandidate): CandidateCellRow {
  const cached = pipelineRows.get(candidate);
  if (cached) return cached;
  const row: CandidateCellRow = {
    profileId: candidate.profile_id,
    fullName: candidate.full_name ?? null,
    avatarUrl: candidate.avatar_url ?? null,
    origin: candidate.origin ?? null,
    jobTitle: candidate.job_title ?? null,
    companyName: candidate.company ?? null,
    companyDomain: candidate.company_domain ?? null,
    linkedinUrl: candidate.linkedin_url ?? null,
    githubUrl: candidate.github_url ?? null,
    twitterUrl: candidate.twitter_url ?? null,
    socialProfiles: candidate.social_profiles ?? [],
  };
  pipelineRows.set(candidate, row);
  return row;
}

export function fromArbitratorRow(source: EvaluationRowSchema): CandidateCellRow {
  const cached = arbitratorRows.get(source);
  if (cached) return cached;
  const card = (source.profile_card ?? {}) as ProfileCardLike;
  const row: CandidateCellRow = {
    profileId: source.profile_id,
    fullName: card.full_name ?? null,
    avatarUrl: card.avatar_url ?? null,
    origin: card.origin ?? null,
    jobTitle: card.job_title ?? null,
    companyName: card.job_company_name ?? null,
    companyDomain: card.job_company_domain ?? null,
    linkedinUrl: card.linkedin_url ?? null,
    githubUrl: card.github_url ?? null,
    twitterUrl: card.twitter_url ?? null,
    socialProfiles: card.social_profiles ?? [],
  };
  arbitratorRows.set(source, row);
  return row;
}
